/**
 * Detail collapse for an assessment.
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Building2, UserCheck, CheckCircle2, FileText, Plus, X } from 'lucide-react';
import { DetailField } from '@/components/common/details/DetailField';
import { DetailBadge } from '@/components/common/details/DetailBadge';
import { DetailsCard } from '@/components/common/DetailsCard';
import { Button } from '@/components/forms/Button';
import { Textarea } from '@/components/forms/Textarea';
import { Input } from '@/components/forms/Input';
import { DatePicker } from '@/components/forms/DatePicker';
import { ValidateModal } from '@/components/common/modals/ValidateModal';
import { usePageActions } from '@/hooks/usePageActions';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';
import {
  getAssessment,
  patchAssessmentPayload,
  submitAssessment,
  validateAssessment,
} from '@/services/assessments.service';
import {
  ASSESSMENT_STATUS_CONFIG,
  ASSESSMENT_TYPE_DEFINITIONS,
  ASSESSMENT_WRITER_LABELS_FR,
  AUTO_EVALUATION_BLOCKS,
  AUTO_EVALUATION_MOMENT_LABELS,
  AUTO_EVALUATION_MOMENTS,
  AUTO_EVALUATION_RATING_LABELS,
  DEFAULT_OVERALL_ASSESSMENT,
  KNOWLEDGE_OPTIONS,
  NAMED_REPORT_TRAININGS,
  SOFT_SKILLS_OPTIONS,
  TECHNICAL_SKILLS_OPTIONS,
} from './assessment-configs';
import {
  addNamedReportMission,
  brandLabel,
  getAssessmentDetailCapabilities,
  shouldFetchSiblingMoments,
  ratingOf,
  remarkOf,
  removeNamedReportMission,
  setNamedReportTrainingDate,
  setRating,
  toggleNamedReportSkill,
  toggleNamedReportTraining,
  updateBlock,
  updateNamedReportMission,
} from '@/components/assessments/assessment-form-utils';
import {
  formatAssessmentDateTime,
  formatAssessmentUserName,
} from '@/components/assessments/assessment-table-utils';
import { getErrorMessage } from '@/utils/errors-utils';
import { getRoleLabel } from '@/utils/role-label-utils';
import type {
  Assessment,
  FreeTextPayload,
  AutoEvaluationBlockEntry,
  AutoEvaluationMoment,
  AutoEvaluationMomentSummary,
  AutoEvaluationPayload,
  AutoEvaluationRating,
  NamedReportPayload,
} from '@/types/assessment.types';
import { useTheme } from '@/hooks/useTheme';

/** NA / ECA / A cells — labels stay the API codes. */
const RATING_OPTIONS: { value: AutoEvaluationRating; label: string }[] = [
  { value: 'NA', label: 'NA' },
  { value: 'ECA', label: 'ECA' },
  { value: 'A', label: 'A' },
];

/** Multi-select chips (skills, trainings). */
const ChipToggleGroup: React.FC<{
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
  disabled?: boolean;
}> = ({ options, selected, onToggle, disabled }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((option) => {
      const isActive = selected.includes(option);
      return (
        <button
          key={option}
          type="button"
          disabled={disabled}
          onClick={() => onToggle(option)}
          className={cn(
            'rounded-full border px-3 py-1.5 text-xs font-medium transition-all',
            isActive
              ? 'border-[var(--color-2-nuance-1)] bg-[color-mix(in_srgb,var(--color-2-nuance-1)_15%,transparent)] text-[var(--color-2-nuance-1)]'
              : 'border-[color-mix(in_srgb,var(--color-1-nuance-3)_40%,transparent)] text-muted-foreground hover:border-[var(--color-1-nuance-2)]',
            disabled && 'opacity-60 cursor-not-allowed',
          )}
        >
          {option}
        </button>
      );
    })}
  </div>
);

/** Free-text payload for `volunteer` / `intermediate` / `other`. */
const FreeTextEditor: React.FC<{
  payload: FreeTextPayload;
  onChange: (payload: FreeTextPayload) => void;
  disabled: boolean;
}> = ({ payload, onChange, disabled }) => (
  <Textarea
    label="Contenu du bilan"
    value={payload.content ?? ''}
    onChange={(e) => onChange({ content: e.target.value })}
    disabled={disabled}
    rows={10}
    placeholder="Rédigez le contenu du bilan…"
  />
);

/** Volunteer self-evaluation grid subtitle. */
const AUTO_EVALUATION_SUBTITLE = 'Grille d’auto-évaluation du volontaire';

/** Placeholder when a moment rating has not been generated. */
const MOMENT_NOT_GENERATED = 'Non généré';

/** NA / ECA / A of one moment: clickable for the assessment being filled, plain reading otherwise. */
const RatingCells: React.FC<{
  value: AutoEvaluationRating | null;
  editable: boolean;
  onSelect?: (rating: AutoEvaluationRating) => void;
}> = ({ value, editable, onSelect }) => {
  const borderClass = editable
    ? 'border-[var(--color-2-nuance-1)]'
    : 'border-[color-mix(in_srgb,var(--color-1-nuance-3)_35%,transparent)]';

  return (
    <div className={cn('inline-flex overflow-hidden rounded-lg border', borderClass)}>
      {RATING_OPTIONS.map((opt, index) => {
        const isActive = value === opt.value;
        const cellClass = cn(
          'px-2.5 py-1 text-xs font-medium transition-all border-0',
          index !== RATING_OPTIONS.length - 1 && cn('border-r', borderClass),
          isActive && editable && 'bg-[var(--color-2-nuance-1)] text-white',
          isActive &&
            !editable &&
            'bg-[color-mix(in_srgb,var(--color-1-nuance-3)_30%,transparent)] text-[var(--color-1-nuance-1)]',
          !isActive && 'bg-transparent text-muted-foreground',
        );
        const title = `${opt.value} : ${AUTO_EVALUATION_RATING_LABELS[opt.value]}`;

        return editable ? (
          <button
            key={opt.value}
            type="button"
            title={title}
            onClick={() => onSelect?.(opt.value)}
            className={cellClass}
          >
            {opt.label}
          </button>
        ) : (
          <span key={opt.value} title={title} className={cellClass}>
            {opt.label}
          </span>
        );
      })}
    </div>
  );
};

/**
 * Shared by every block so start / mid / end-of-mission columns stay on the same
 * verticals down the page: without fixed widths each block sizes its own columns from its longest
 * competence label and the moments drift sideways from one block to the next.
 */
const GridColumns: React.FC = () => (
  <colgroup>
    <col className="w-[40%]" />
    {AUTO_EVALUATION_MOMENTS.map((moment) => (
      <col key={moment} className="w-[20%]" />
    ))}
  </colgroup>
);

/** Self-evaluation grid: current moment is editable; sibling moments are read-only. */
const AutoEvaluationEditor: React.FC<{
  payload: AutoEvaluationPayload;
  /** Grid column filled by this assessment (start / mid / end of mission). */
  moment?: AutoEvaluationMoment;
  /** The other moments of the same VSC, shown read-only so the whole grid is visible. */
  siblings?: Partial<Record<AutoEvaluationMoment, AutoEvaluationMomentSummary>>;
  onChange: (payload: AutoEvaluationPayload) => void;
  disabled: boolean;
}> = ({ payload, moment, siblings, onChange, disabled }) => {
  const { companyName } = useTheme();

  const handleUpdateBlock = (blockId: string, changes: Partial<AutoEvaluationBlockEntry>) => {
    onChange(updateBlock(payload, blockId, changes));
  };

  const handleSetRating = (blockId: string, itemId: string, rating: AutoEvaluationRating) => {
    onChange(setRating(payload, blockId, itemId, rating));
  };

  const ratingAt = (
    target: AutoEvaluationMoment,
    blockId: string,
    itemId: string,
  ): AutoEvaluationRating | null => ratingOf(payload, moment, siblings, target, blockId, itemId);

  const remarkAt = (target: AutoEvaluationMoment, blockId: string): string =>
    remarkOf(payload, moment, siblings, target, blockId);

  const isCurrent = (target: AutoEvaluationMoment) => target === moment;
  const exists = (target: AutoEvaluationMoment) => isCurrent(target) || !!siblings?.[target];

  const momentHint = (target: AutoEvaluationMoment): string => {
    if (isCurrent(target)) return disabled ? 'Ce bilan' : 'Renseigné ici';
    const sibling = siblings?.[target];
    return sibling ? ASSESSMENT_STATUS_CONFIG[sibling.status].label : MOMENT_NOT_GENERATED;
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <h5 className="text-sm font-semibold text-[var(--color-1-nuance-1)]">
          {AUTO_EVALUATION_SUBTITLE}
        </h5>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded-lg border border-[color-mix(in_srgb,var(--color-1-nuance-3)_30%,transparent)] bg-[color-mix(in_srgb,var(--color-1-nuance-3)_8%,transparent)] px-3 py-2">
          {RATING_OPTIONS.map((opt) => (
            <span key={opt.value} className="text-xs text-muted-foreground">
              <span className="font-semibold text-[var(--color-1-nuance-1)]">{opt.value}</span> :{' '}
              {AUTO_EVALUATION_RATING_LABELS[opt.value]}
            </span>
          ))}
        </div>
        {moment && (
          <p className="text-xs text-muted-foreground">
            Colonne renseignée ici :{' '}
            <span className="font-medium">{AUTO_EVALUATION_MOMENT_LABELS[moment]}</span>. Les autres
            moments sont affichés en lecture seule et se remplissent depuis leur propre bilan ;
            l’export reprend les trois colonnes.
          </p>
        )}
      </div>

      {/* One scroller for the whole grid: per-block scrollers would let the moment columns of two
          blocks show at different offsets. */}
      <div className="overflow-x-auto">
        <div className="min-w-[46rem] space-y-5">
          {AUTO_EVALUATION_BLOCKS.map((block) => (
            <div
              key={block.id}
              className="rounded-xl border border-[color-mix(in_srgb,var(--color-1-nuance-3)_30%,transparent)] overflow-hidden"
            >
              <h5 className="bg-[color-mix(in_srgb,var(--color-1-nuance-3)_12%,transparent)] px-4 py-2 text-sm font-semibold text-[var(--color-1-nuance-1)]">
                {block.numeral}. {block.title}
              </h5>

              <table className="w-full table-fixed border-collapse text-sm">
                <GridColumns />
                <thead>
                  <tr className="border-b border-[color-mix(in_srgb,var(--color-1-nuance-3)_20%,transparent)]">
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">
                      Compétence
                    </th>
                    {AUTO_EVALUATION_MOMENTS.map((target) => (
                      <th
                        key={target}
                        className={cn(
                          'px-3 py-2 text-center text-xs font-semibold whitespace-nowrap',
                          isCurrent(target)
                            ? 'bg-[color-mix(in_srgb,var(--color-2-nuance-1)_10%,transparent)] text-[var(--color-2-nuance-1)]'
                            : 'text-[var(--color-1-nuance-2)]',
                        )}
                      >
                        {AUTO_EVALUATION_MOMENT_LABELS[target]}
                        <span className="block text-[10px] font-normal text-muted-foreground">
                          {momentHint(target)}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[color-mix(in_srgb,var(--color-1-nuance-3)_20%,transparent)]">
                  {block.items.map((item) => (
                    <tr key={item.id}>
                      <td className="px-4 py-2.5 text-sm text-foreground align-top">
                        {brandLabel(item.label, companyName)}
                      </td>
                      {AUTO_EVALUATION_MOMENTS.map((target) => (
                        <td
                          key={target}
                          className={cn(
                            'px-3 py-2.5 text-center align-top',
                            isCurrent(target) &&
                              'bg-[color-mix(in_srgb,var(--color-2-nuance-1)_6%,transparent)]',
                          )}
                        >
                          {exists(target) ? (
                            <RatingCells
                              value={ratingAt(target, block.id, item.id)}
                              editable={isCurrent(target) && !disabled}
                              onSelect={(rating) => handleSetRating(block.id, item.id, rating)}
                            />
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="space-y-3 bg-[color-mix(in_srgb,var(--color-1-nuance-3)_6%,transparent)] px-4 py-3">
                {moment && (
                  <Textarea
                    label={`Remarques — ${AUTO_EVALUATION_MOMENT_LABELS[moment]}`}
                    value={payload.blocks?.[block.id]?.remark ?? ''}
                    onChange={(e) => handleUpdateBlock(block.id, { remark: e.target.value })}
                    disabled={disabled}
                    rows={2}
                  />
                )}
                {AUTO_EVALUATION_MOMENTS.filter(
                  (target) => !isCurrent(target) && remarkAt(target, block.id),
                ).map((target) => (
                  <div key={target} className="text-xs text-muted-foreground">
                    <span className="font-medium text-[var(--color-1-nuance-2)]">
                      Remarques — {AUTO_EVALUATION_MOMENT_LABELS[target]} :
                    </span>{' '}
                    {remarkAt(target, block.id)}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/** Named-report payload: skills chips, trainings, missions, overall assessment. */
const NamedReportEditor: React.FC<{
  payload: NamedReportPayload;
  onChange: (payload: NamedReportPayload) => void;
  disabled: boolean;
}> = ({ payload, onChange, disabled }) => {
  const { companyName } = useTheme();
  const knowledgeOptions = React.useMemo(
    () => KNOWLEDGE_OPTIONS.map((option) => brandLabel(option, companyName)),
    [companyName],
  );
  const missionKeysRef = React.useRef<string[]>([]);

  /** Stable React keys for mission rows (do not key on the editable text). */
  const missionKeyAt = (index: number): string => {
    const keys = missionKeysRef.current;
    while (keys.length <= index) {
      keys.push(crypto.randomUUID());
    }
    return keys[index];
  };

  const updateMission = (index: number, value: string) => {
    onChange(updateNamedReportMission(payload, index, value));
  };

  const removeMission = (index: number) => {
    missionKeysRef.current.splice(index, 1);
    onChange(removeNamedReportMission(payload, index));
  };

  const addMission = () => {
    missionKeysRef.current.push(crypto.randomUUID());
    onChange(addNamedReportMission(payload));
  };

  return (
    <div className="space-y-6">
      <div className="md:max-w-xs">
        <DatePicker
          label="Date de naissance"
          value={payload.assessment.birthDate ?? ''}
          onChange={(value) =>
            onChange({
              ...payload,
              assessment: { ...payload.assessment, birthDate: value || null },
            })
          }
          disabled={disabled}
          required
          maxDate={new Date().toISOString().split('T')[0]}
          usePortal
        />
      </div>

      <div className="space-y-2">
        <span className="text-xs font-medium text-muted-foreground">Savoir-être</span>
        <ChipToggleGroup
          options={SOFT_SKILLS_OPTIONS}
          selected={payload.skills.softSkills}
          disabled={disabled}
          onToggle={(v) => onChange(toggleNamedReportSkill(payload, 'softSkills', v))}
        />
      </div>

      <div className="space-y-2">
        <span className="text-xs font-medium text-muted-foreground">Savoir-faire</span>
        <ChipToggleGroup
          options={TECHNICAL_SKILLS_OPTIONS}
          selected={payload.skills.technicalSkills}
          disabled={disabled}
          onToggle={(v) => onChange(toggleNamedReportSkill(payload, 'technicalSkills', v))}
        />
      </div>

      <div className="space-y-2">
        <span className="text-xs font-medium text-muted-foreground">Savoirs</span>
        <ChipToggleGroup
          options={knowledgeOptions}
          selected={payload.skills.knowledge}
          disabled={disabled}
          onToggle={(v) => onChange(toggleNamedReportSkill(payload, 'knowledge', v))}
        />
      </div>

      <Textarea
        label="Complément (compétences)"
        value={payload.skills.additionalNotes ?? ''}
        onChange={(e) =>
          onChange({ ...payload, skills: { ...payload.skills, additionalNotes: e.target.value } })
        }
        disabled={disabled}
        rows={2}
      />

      <div className="space-y-2">
        <span className="text-xs font-medium text-muted-foreground">Formations suivies</span>
        <div className="space-y-2">
          {NAMED_REPORT_TRAININGS.map((training) => {
            const isChecked = payload.trainings.completed.includes(training.id);
            return (
              <div key={training.id} className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    disabled={disabled}
                    checked={isChecked}
                    onChange={() => onChange(toggleNamedReportTraining(payload, training.id))}
                  />
                  {brandLabel(training.label, companyName)}
                </label>
                {training.hasDate && isChecked && (
                  <div className="w-44">
                    <DatePicker
                      value={payload.trainings.dates[training.id] ?? ''}
                      onChange={(value) =>
                        onChange(setNamedReportTrainingDate(payload, training.id, value))
                      }
                      disabled={disabled}
                      inputSize="sm"
                      placeholder="Date"
                      usePortal
                    />
                  </div>
                )}
                {training.hasTitle && isChecked && (
                  <input
                    type="text"
                    disabled={disabled}
                    value={payload.trainings.civicServiceTitle ?? ''}
                    onChange={(e) =>
                      onChange({
                        ...payload,
                        trainings: { ...payload.trainings, civicServiceTitle: e.target.value },
                      })
                    }
                    placeholder="Intitulé de la formation…"
                    className="flex-1 min-w-[12rem] rounded-lg border border-[color-mix(in_srgb,var(--color-1-nuance-3)_30%,transparent)] bg-[var(--card)] px-2 py-1 text-xs"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Input
        label="Nom du tuteur / de la tutrice"
        value={payload.assessment.mentorName ?? ''}
        onChange={(e) =>
          onChange({
            ...payload,
            assessment: { ...payload.assessment, mentorName: e.target.value },
          })
        }
        disabled={disabled}
      />

      <div className="space-y-2">
        <span className="text-xs font-medium text-muted-foreground">Missions réalisées</span>
        <div className="space-y-2">
          {payload.assessment.completedMissions.map((mission: string, index: number) => (
            <div key={missionKeyAt(index)} className="flex items-center gap-2">
              <input
                type="text"
                disabled={disabled}
                value={mission}
                onChange={(e) => updateMission(index, e.target.value)}
                className="flex-1 rounded-lg border border-[color-mix(in_srgb,var(--color-1-nuance-3)_30%,transparent)] bg-[var(--card)] px-3 py-1.5 text-sm"
              />
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeMission(index)}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          {!disabled && (
            <Button
              variant="outline"
              size="sm"
              icon={Plus}
              iconPosition="left"
              onClick={addMission}
            >
              Ajouter une mission
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-1">
        <Textarea
          label="Appréciation globale"
          value={payload.assessment.overallAssessment ?? ''}
          onChange={(e) =>
            onChange({
              ...payload,
              assessment: { ...payload.assessment, overallAssessment: e.target.value },
            })
          }
          disabled={disabled}
          rows={5}
          required
          placeholder={DEFAULT_OVERALL_ASSESSMENT}
        />
        <p className="text-xs text-muted-foreground">
          Requis pour soumettre. Tant que le champ est vide, l’aperçu PDF reprend l’appréciation
          type du modèle{companyName ? ` ${companyName}` : ''}.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Lieu"
          value={payload.assessment.location ?? ''}
          onChange={(e) =>
            onChange({
              ...payload,
              assessment: { ...payload.assessment, location: e.target.value },
            })
          }
          disabled={disabled}
        />
        <DatePicker
          label="Date"
          value={payload.assessment.date ?? ''}
          onChange={(value) =>
            onChange({ ...payload, assessment: { ...payload.assessment, date: value || null } })
          }
          disabled={disabled}
          usePortal
          placement="top"
        />
      </div>
    </div>
  );
};

/**
 * Detail / edit body for one assessment (table collapse or {@link EditAssessmentView}).
 * Schema `bilan_auto_evaluation` / `bilan_named` / free-text; submit then validate.
 */
export const AssessmentDetailsPanel: React.FC<{
  /** Assessment shown in the collapse. */
  assessment: Assessment;
  /** `view` locks the form; `edit` is the table-collapse default. */
  presentationMode?: 'view' | 'edit';
  /** Hide the title/badge header when the parent already renders one. */
  hideDetailHeader?: boolean;
  /** Called after submit/validate so the parent can collapse or refetch. */
  onActionSuccess?: () => void;
}> = ({ assessment, presentationMode = 'edit', hideDetailHeader = false, onActionSuccess }) => {
  const queryClient = useQueryClient();
  const { canEditRow, canValidate } = usePageActions('assessments');
  const roleCodes = useAuthStore((s) => s.roles).map((r) => r.code);
  const [draft, setDraft] = React.useState<Record<string, unknown>>(assessment.payload);
  const [validateOpen, setValidateOpen] = React.useState(false);

  /** Re-hydrate the draft when a different assessment is opened or the server payload changes. */
  React.useEffect(() => {
    setDraft(assessment.payload);
  }, [assessment.id, assessment.payload]);

  /**
   * Rows coming from the list have no sibling grids: only `GET /bilans/:id` carries them, so the
   * self-evaluation panel loads the detail to display the two other moments of the grid.
   */
  const needsSiblingMoments = shouldFetchSiblingMoments(assessment);
  const { data: detail } = useQuery({
    queryKey: ['assessment', assessment.id],
    queryFn: () => getAssessment(assessment.id),
    enabled: needsSiblingMoments,
  });
  const siblingMoments = assessment.autoEvaluationMoments ?? detail?.autoEvaluationMoments;

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['assessments'], refetchType: 'all' }),
      queryClient.invalidateQueries({ queryKey: ['assessment'], refetchType: 'all' }),
    ]);

  const submitMutation = useMutation({
    mutationFn: async () => {
      await patchAssessmentPayload(assessment.id, { payload: draft });
      return submitAssessment(assessment.id);
    },
    onSuccess: async () => {
      await invalidate();
      toast.success('Bilan soumis');
      onActionSuccess?.();
    },
    onError: (e: unknown) => {
      toast.error(getErrorMessage(e));
    },
  });

  const validateMutation = useMutation({
    mutationFn: () => validateAssessment(assessment.id),
    onSuccess: async () => {
      await invalidate();
      toast.success('Bilan validé');
      setValidateOpen(false);
      onActionSuccess?.();
    },
    onError: (e: unknown) => {
      toast.error(getErrorMessage(e));
    },
  });

  const def = ASSESSMENT_TYPE_DEFINITIONS[assessment.type];
  const statusConfig = ASSESSMENT_STATUS_CONFIG[assessment.status];
  const StatusIcon = statusConfig.icon;

  const { editable, formDisabled, canValidateUi } = getAssessmentDetailCapabilities({
    presentationMode,
    status: assessment.status,
    type: assessment.type,
    canEditRow,
    canValidate,
    roleCodes,
  });

  /** Editor by `schemaId` — auto-eval grid, named report, or free text. */
  const renderEditor = () => {
    switch (assessment.schemaId) {
      case 'bilan_auto_evaluation':
        return (
          <AutoEvaluationEditor
            payload={draft as unknown as AutoEvaluationPayload}
            moment={
              (AUTO_EVALUATION_MOMENTS as readonly string[]).includes(assessment.type)
                ? (assessment.type as AutoEvaluationMoment)
                : undefined
            }
            siblings={siblingMoments}
            onChange={setDraft as unknown as (p: AutoEvaluationPayload) => void}
            disabled={formDisabled}
          />
        );
      case 'bilan_named':
        return (
          <NamedReportEditor
            payload={draft as unknown as NamedReportPayload}
            onChange={setDraft as unknown as (p: NamedReportPayload) => void}
            disabled={formDisabled}
          />
        );
      default:
        return (
          <FreeTextEditor
            payload={draft as unknown as FreeTextPayload}
            onChange={setDraft as unknown as (p: FreeTextPayload) => void}
            disabled={formDisabled}
          />
        );
    }
  };

  return (
    <>
      {!hideDetailHeader ? (
        <div className="details-view-header">
          <div className="details-view-header-left">
            <div className="details-view-header-icon">
              <ClipboardList className="w-7 h-7 text-[var(--color-2-nuance-1)]" />
            </div>
            <div className="details-view-header-content">
              <div className="details-view-header-with-badges">
                <h3 className="text-2xl font-bold text-[var(--color-1-nuance-1)]">{def.label}</h3>
                <DetailBadge
                  label={statusConfig.label}
                  icon={StatusIcon}
                  colorName={assessment.status === 'validated' ? 'success' : 'error'}
                  variant="filled"
                />
              </div>
              <p className="text-base text-[var(--color-1-nuance-2)]">
                {assessment.establishment?.name ?? def.label}
              </p>
            </div>
          </div>
        </div>
      ) : null}

      <div className="details-view-grid">
        <DetailsCard icon={UserCheck} title="Personne concernée" colorName="color-3" colSpan={1}>
          <DetailField
            label={
              assessment.targetUser
                ? `${getRoleLabel('volunteer')} / intervenant`
                : getRoleLabel('coordinator')
            }
            value={formatAssessmentUserName(assessment.targetUser ?? assessment.coordinatorUser)}
            vertical
          />
          <DetailField
            label="Rempli par"
            value={
              def.writtenBy === 'coordinator' && assessment.coordinatorUser
                ? formatAssessmentUserName(assessment.coordinatorUser)
                : ASSESSMENT_WRITER_LABELS_FR[def.writtenBy]
            }
            vertical
          />
        </DetailsCard>

        {assessment.establishment && (
          <DetailsCard icon={Building2} title="Établissement" colorName="color-1" colSpan={1}>
            <DetailField label="Nom" value={assessment.establishment.name} vertical />
          </DetailsCard>
        )}

        <DetailsCard icon={FileText} title="Suivi" colorName="color-2" colSpan={1}>
          <div className="space-y-2">
            <DetailField
              label="Généré le"
              value={formatAssessmentDateTime(assessment.generatedAt)}
            />
            {assessment.submittedAt && (
              <DetailField
                label="Soumis le"
                value={formatAssessmentDateTime(assessment.submittedAt)}
              />
            )}
            {assessment.validatedAt && (
              <DetailField
                label="Validé le"
                value={formatAssessmentDateTime(assessment.validatedAt)}
              />
            )}
          </div>
        </DetailsCard>

        <DetailsCard
          icon={ClipboardList}
          title="Contenu du bilan"
          colorName="color-2"
          colSpan="full"
        >
          <div className={cn('space-y-4', formDisabled && 'opacity-90')}>
            {renderEditor()}

            {presentationMode !== 'view' ? (
              <div className="flex flex-wrap gap-2 pt-2">
                {editable && (
                  <Button
                    preset="modal-confirm"
                    disabled={submitMutation.isPending}
                    loading={submitMutation.isPending}
                    onClick={() => submitMutation.mutate()}
                  >
                    Soumettre
                  </Button>
                )}
                {canValidateUi && (
                  <Button
                    preset="modal-confirm"
                    icon={CheckCircle2}
                    disabled={validateMutation.isPending}
                    loading={validateMutation.isPending}
                    onClick={() => setValidateOpen(true)}
                  >
                    Valider
                  </Button>
                )}
              </div>
            ) : null}
          </div>
        </DetailsCard>
      </div>

      <ValidateModal
        open={validateOpen}
        onClose={() => setValidateOpen(false)}
        onConfirm={() => void validateMutation.mutateAsync()}
        itemName="ce bilan"
        isLoading={validateMutation.isPending}
        description="Confirmer la validation ? Seuls les rôles autorisés côté serveur peuvent valider."
      />
    </>
  );
};

/** Default expanded-row renderer (edit mode). */
export const renderAssessmentDetails = (assessment: Assessment): React.ReactNode => (
  <AssessmentDetailsPanel assessment={assessment} presentationMode="edit" />
);
